import { beginSandboxEnsurePreviewCycle, reloadSandboxPreview, setSandboxPreviewUrl, setTargetLayer, tracePreview } from '@/helpers/preview/helper';
import { previewStartupTracker } from '@/helpers/preview/previewStartupTrackingRuntime';
import { ensureProjectSandbox, getProjectSandbox } from '@/services/project';
import { usePreviewStore } from '@/stores/preview';
import { useProjectStore } from '@/stores/project';
import { useUserStore } from '@/stores/user';
import { isProjectSandboxNotFoundError, isProjectSandboxTimeoutError, isProjectSandboxUnavailableError, shouldPreserveReadySandboxPreview, shouldPreserveSandboxPreview, shouldReuseSandboxRuntime } from './sandboxError';
let ensuringProjectId = '';
let ensurePromise = null;
let ensureRequestGeneration = 0;
/**
 * 只查询当前 Sandbox 持久化快照，不把快照状态当成 runtime 可用证明。
 */ export async function queryProjectSandbox(projectId) {
    try {
        const { data } = await getProjectSandbox(projectId);
        // 无映射是成功的空状态；悬空映射由 API 返回 300008，也按空快照交给后续 ensure 修复。
        return data;
    } catch (error) {
        if (!isProjectSandboxNotFoundError(error)) {
            console.warn('[sandbox] Failed to query project Sandbox snapshot:', error);
        }
        return null;
    }
}
/**
 * 确保当前项目的 Sandbox 可用。相同项目的并发调用共享一个请求，避免初始化、心跳恢复和发送消息重复创建。
 */ export function ensureSandbox(projectId, sessionId, userId) {
    let options = arguments.length > 3 && arguments[3] !== void 0 ? arguments[3] : {};
    if (ensuringProjectId === projectId && ensurePromise) {
        tracePreview('sandbox.ensure-reused', {
            projectId
        });
        return ensurePromise;
    }
    const requestGeneration = ++ensureRequestGeneration;
    const previewState = usePreviewStore.getState();
    const projectState = useProjectStore.getState();
    const runtimeState = {
        sandboxStatus: projectState.sandboxStatus,
        sandboxId: projectState.sandboxId,
        sandboxUrl: previewState.sandboxUrl,
        livePreviewStatus: previewState.livePreviewStatus,
        sandboxRuntimeStatus: previewState.sandboxRuntimeStatus
    };
    const isCurrentProject = projectState.projectId === projectId;
    const shouldPreserveReadyPreview = options.background === true && shouldPreserveReadySandboxPreview({
        ...runtimeState,
        sandboxIframeReady: previewState.sandboxIframeReady
    });
    const canReuseCurrentRuntime = isCurrentProject && (shouldReuseSandboxRuntime(runtimeState) || shouldPreserveReadyPreview);
    const shouldPreserveCurrentPreview = isCurrentProject && (shouldPreserveSandboxPreview(runtimeState) || shouldPreserveReadyPreview);
    if (!shouldPreserveCurrentPreview) {
        previewStartupTracker.start('sandbox_ensure');
        beginSandboxEnsurePreviewCycle();
        projectState.setSandboxStatus('unknown');
    }
    tracePreview('sandbox.ensure-started', {
        projectId,
        preserveCurrentPreview: shouldPreserveCurrentPreview
    });
    ensuringProjectId = projectId;
    ensurePromise = ensureProjectSandbox(projectId, sessionId).then((param)=>{
        let { data } = param;
        if (!data.url) {
            throw new Error(intl.get('project.error.noSandboxUrl') || '沙箱地址不可用');
        }
        if (ensureRequestGeneration !== requestGeneration || useProjectStore.getState().projectId !== projectId) {
            return;
        }
        useProjectStore.getState().setSandboxId(data.sandboxId);
        useProjectStore.getState().setUserId(userId);
        useProjectStore.getState().setSessionId(sessionId);
        if (!canReuseCurrentRuntime && previewState.sandboxUrl === data.url) {
            reloadSandboxPreview();
        } else {
            setSandboxPreviewUrl(data.url);
        }
        tracePreview('sandbox.ensure-succeeded', {
            projectId,
            sandboxId: data.sandboxId
        });
    }).catch((error)=>{
        const shouldFallbackStable = isProjectSandboxUnavailableError(error) || isProjectSandboxTimeoutError(error);
        if (ensureRequestGeneration === requestGeneration && !shouldPreserveCurrentPreview && useProjectStore.getState().projectId === projectId) {
            const state = usePreviewStore.getState();
            state.setLivePreviewStatus('error');
            state.setSandboxRuntimeStatus('error');
            state.setLivePreviewProblem(shouldFallbackStable ? 'sandbox-unavailable' : 'runtime-error');
            previewStartupTracker.fail(isProjectSandboxTimeoutError(error) ? 'sandbox_ensure_timeout' : isProjectSandboxUnavailableError(error) ? 'sandbox_unavailable' : 'sandbox_ensure_failed');
            tracePreview('sandbox.ensure-failed', {
                projectId,
                problem: shouldFallbackStable ? 'sandbox-unavailable' : 'runtime-error'
            });
        }
        throw error;
    }).finally(()=>{
        if (ensureRequestGeneration === requestGeneration && ensuringProjectId === projectId) {
            ensuringProjectId = '';
            ensurePromise = null;
        }
    });
    return ensurePromise;
}
/**
 * 确保当前项目已有可供 Agent 使用的 Sandbox runtime。
 */ export function getOrEnsureSandbox(projectId) {
    let options = arguments.length > 1 && arguments[1] !== void 0 ? arguments[1] : {};
    var _useUserStore_getState_userInfo;
    const projectState = useProjectStore.getState();
    const previewState = usePreviewStore.getState();
    if (projectState.projectId !== projectId) {
        return Promise.reject(new Error('Project changed before Sandbox ensure'));
    }
    if (ensuringProjectId === projectId && ensurePromise) {
        tracePreview('sandbox.ensure-reused', {
            projectId
        });
        return ensurePromise;
    }
    if (shouldReuseSandboxRuntime({
        sandboxStatus: projectState.sandboxStatus,
        sandboxId: projectState.sandboxId,
        sandboxUrl: previewState.sandboxUrl,
        livePreviewStatus: previewState.livePreviewStatus,
        sandboxRuntimeStatus: previewState.sandboxRuntimeStatus
    })) {
        tracePreview('sandbox.ensure-skipped', {
            projectId,
            reason: 'already-available'
        });
        return Promise.resolve();
    }
    if (!options.background) {
        setTargetLayer('live');
    }
    tracePreview('sandbox.requested', {
        projectId
    });
    const userId = projectState.userId || ((_useUserStore_getState_userInfo = useUserStore.getState().userInfo) === null || _useUserStore_getState_userInfo === void 0 ? void 0 : _useUserStore_getState_userInfo.userId) || 'system';
    const sessionId = projectState.sessionId || `chat-${projectId}-${userId}`;
    return ensureSandbox(projectId, sessionId, userId, options);
}
export function resetSandboxEnsure() {
    ensureRequestGeneration += 1;
    ensuringProjectId = '';
    ensurePromise = null;
    previewStartupTracker.reset();
}
